import { diagnostic } from '../utils/diagnostics';
import { readAllRows } from '../utils/readAllRows';
import supabase, { supabaseKid } from "../supabase/supabase";
import { getAppleIdentity } from "./appleSignIn";

// Table names with timestamp suffix for safety
// Explicit child read allowlist: never request pin_hash from client APIs.
const CHILD_SAFE_COLUMNS = "id,family_id,nickname,age,avatar,status,created_at,full_name,user_id,avatar_url,auth_user_id,require_chore_approval,dashboard_theme,age_group,first_name,middle_initial,last_name,suffix,onboarding_status,onboarding_step,onboarding_version,onboarding_started_at,onboarding_completed_at,starting_money_set_at,starting_money_transaction_id,deactivated_at,deactivated_by_parent_id,reactivated_at,reactivated_by_parent_id";

const TABLES = {
  FAMILIES: "families_1740480000000",
  PARENTS: "parents_1740480000000",
  CHILDREN: "children_1740480000000",
  TRANSACTIONS: "transactions_1740480000000",
  SAVINGS_GOALS: "savings_goals_1740480000000",
  SAVINGS_LEDGER: "savings_ledger_1740480000000",
  CHORES: "chores_1740480000000",
  CHORE_CLAIMS: "chore_claims_1740480000000",
};

export { supabase, supabaseKid, TABLES };

// --------------------
// Parent Auth helpers
// --------------------
export const auth = {
  signUp: (email, password, options = {}) =>
    supabase.auth.signUp({
      email,
      password,
      options,
    }),

  signIn: (email, password) =>
    supabase.auth.signInWithPassword({
      email,
      password,
    }),

  signInWithGoogle: ({
    redirectTo,
    skipBrowserRedirect = false,
  }) =>
    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect,
        queryParams: {
          prompt: 'select_account',
        },
      },
    }),

  signInWithAppleWeb: ({ redirectTo }) =>
    supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo },
    }),

  signInWithApple: async () => {
    const { identityToken, rawNonce, givenName, familyName } =
      await getAppleIdentity();

    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: identityToken,
      nonce: rawNonce,
    });

    if (error) throw error;

    // Apple shares the name only on initial authorization. Preserve
    // existing profile metadata and never replace an established name.
    const name = [givenName, familyName].filter(Boolean).join(' ').trim();
    if (name && data?.user &&
        !data.user.user_metadata?.name &&
        !data.user.user_metadata?.full_name) {
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { name, full_name: name },
      });
      if (metadataError) {
        // Authentication succeeded. Do not treat optional metadata as a login failure.
        diagnostic('warn', 'parent.apple.metadata_warning');
      }
    }

    return data;
  },

  signOut: async () => {
    const { detachPush } = await import('./pushNotifications');
    await detachPush(supabase, 'parent');
    return supabase.auth.signOut();
  },
  getSession: () => supabase.auth.getSession(),
  getUser: () => supabase.auth.getUser(),
  onAuthStateChange: (callback) => supabase.auth.onAuthStateChange(callback),
};

// --------------------
// Parent DB helpers
// --------------------
export const db = {
  resolveMyParentAuthState: async () => {
  const {
    data,
    error,
  } = await supabase.rpc(
    'resolve_my_parent_auth_state'
  );

  if (error) {
    throw error;
  }

  if (
    !Array.isArray(data) ||
    data.length !== 1
  ) {
    throw new Error(
      'Unable to determine the authenticated parent account state.'
    );
    }

    return data[0];
  },

  resolveMyFamilyAccessState: async () => {
    const {
      data,
      error,
    } = await supabase.rpc(
      'resolve_my_family_access_state'
    );

    if (error) {
      throw error;
    }

    if (
      !Array.isArray(data) ||
      data.length !== 1
    ) {
      throw new Error(
        'Unable to determine the family access state.'
      );
    }

    return data[0];
  },
  // Families
  createFamily: async (familyData) => {
    const { data, error } = await supabase
      .from(TABLES.FAMILIES)
      .insert(familyData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Parents
  createParent: async (parentData) => {
    const { data, error } = await supabase
      .from(TABLES.PARENTS)
      .insert(parentData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  getParent: async (authUserId, { signal } = {}) => {
    let query = supabase
      .from(TABLES.PARENTS)
      .select(`*, ${TABLES.FAMILIES}(*)`)
      .eq("auth_user_id", authUserId)
      .maybeSingle();

    if (signal) query = query.abortSignal(signal);

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  updateParentTheme: async (themePreference) => {
    const { data, error } = await supabase.rpc(
      "update_parent_theme",
      {
        p_theme: themePreference,
      }
    );

    if (error) throw error;
    return data;
  },

  updateMyParentName: async (name) => {
    const { data, error } = await supabase.rpc(
      "update_my_parent_name",
      {
        p_name: name,
      }
    );

    if (error) throw error;
    return data;
  },

  getFamily: async (familyId) => {
    const { data, error } = await supabase
      .from(TABLES.FAMILIES)
      .select("id, kid_family_code, display_name, time_zone")
      .eq("id", familyId)
      .single();
    if (error) throw error;
    return data;
  },

  updateMyFamilyDisplayName: async (displayName) => {
    const { data, error } = await supabase.rpc(
      "update_my_family_display_name",
      {
        p_display_name: displayName,
      }
    );

    if (error) throw error;
    return data;
  },

  updateMyParentOnboarding: async (
    status,
    step
  ) => {
    const { data, error } = await supabase.rpc(
      "update_my_parent_onboarding",
      {
        p_status: status,
        p_step: step,
      }
    );

    if (error) throw error;
    return data;
  },

  // Children
  getChildren: async (familyId) => {
    const { data, error } = await supabase
      .from(TABLES.CHILDREN)
      .select(CHILD_SAFE_COLUMNS)
      .eq("family_id", familyId)
      .order("status", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw error;
    return data;
  },

  updateMyFamilyTimeZone: async (timeZone) => {
    const { data, error } = await supabase.rpc(
      "update_my_family_time_zone",
      {
        p_time_zone: timeZone,
      }
    );

    if (error) throw error;
    return data;
  },

  getActiveChildren: async (familyId) => {
    const { data, error } = await supabase
      .from(TABLES.CHILDREN)
      .select(CHILD_SAFE_COLUMNS)
      .eq("family_id", familyId)
      .eq("status", "active")
      .order("created_at", { ascending: true });
    if (error) throw error;
    return data;
  },

  createChild: async (childData) => {
    const { data, error } = await supabase
      .from(TABLES.CHILDREN)
      .insert(childData)
      .select(CHILD_SAFE_COLUMNS)
      .single();
    if (error) throw error;
    return data;
  },

  updateChildProfile: async (childId, profile) => {
    const { data, error } = await supabase.rpc(
      "update_my_family_child_profile",
      {
        p_child_id: childId,
        p_first_name: profile.first_name,
        p_middle_initial: profile.middle_initial || null,
        p_last_name: profile.last_name,
        p_suffix: profile.suffix || null,
        p_nickname: profile.nickname,
        p_age_group: profile.age_group,
      }
    );

    if (error) throw error;
    return data;
  },

  deactivateChild: async (childId) => {
    const { data, error } = await supabase.rpc(
      "deactivate_my_family_child",
      { p_child_id: childId }
    );

    if (error) throw error;
    return data;
  },

  reactivateChild: async (childId) => {
    const { data, error } = await supabase.rpc(
      "reactivate_my_family_child",
      { p_child_id: childId }
    );

    if (error) throw error;
    return data;
  },
  updateChildChoreApproval: async (
    childId,
    requireChoreApproval
  ) => {
    const { data, error } = await supabase
      .from(TABLES.CHILDREN)
      .update({
        require_chore_approval:
          requireChoreApproval,
      })
      .eq('id', childId)
      .select(CHILD_SAFE_COLUMNS)
      .single();
  
    if (error) {
      throw error;
    }
  
    return data;
  },
    
    changeChildPin: async (childId, newPin) => {
    const { data, error } = await supabase.functions.invoke(
      'change_kid_pin',
      {
        body: {
          childId,
          newPin,
        },
      }
    );

    if (error) {
      let serverMessage = null;

      try {
        const response = error.context;
        if (response && typeof response.clone === 'function') {
          const body = await response.clone().json();
          serverMessage = body?.error || body?.message || null;
        }
      } catch {
        // Network errors and non-JSON responses have no function message.
      }

      throw new Error(
        serverMessage ||
          error.message ||
          'Unable to change child password'
      );
    }

    if (!data?.success) {
      throw new Error(
        data?.error ||
          'Unable to change child password'
      );
    }

    return data;
  },
  
  getChild: async (childId) => {
    const { data, error } = await supabase
      .from(TABLES.CHILDREN)
      .select(CHILD_SAFE_COLUMNS)
      .eq("id", childId)
      .single();
    if (error) throw error;
    return data;
  },

  // Transactions
  getTransactions: async (familyId, childId = null, { allPages = false } = {}) => {
    const createQuery = () => {
      let query = supabase.from(TABLES.TRANSACTIONS)
        .select('*', allPages ? { count: 'exact' } : {})
        .eq('family_id', familyId)
        .order('created_at', { ascending: false });
      if (allPages) query = query.order('id', { ascending: false });
      if (childId) query = query.eq('child_id', childId);
      return query;
    };
    if (allPages) return readAllRows(createQuery);
    const { data, error } = await createQuery();
    if (error) throw error;
    return data;
  },

  createTransaction: async (transactionData) => {
    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .insert(transactionData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  updateTransactionApproval: async (transactionId, approvalStatus) => {
    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .update({ approval_status: approvalStatus })
      .eq('id', transactionId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Savings Goals
  getSavingsGoals: async (childId, { allPages = false } = {}) => {
    const createQuery = () => {
      let query = supabase.from(TABLES.SAVINGS_GOALS)
        .select('*', allPages ? { count: 'exact' } : {})
        .eq('child_id', childId)
        .order('created_at', { ascending: false });
      if (allPages) query = query.order('id', { ascending: false });
      return query;
    };
    if (allPages) return readAllRows(createQuery);
    const { data, error } = await createQuery();
    if (error) throw error;
    return data;
  },

  createSavingsGoal: async (goalData) => {
    const { data, error } = await supabase
      .from(TABLES.SAVINGS_GOALS)
      .insert(goalData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  updateSavingsGoal: async (goalId, updates) => {
    const { data, error } = await supabase
      .from(TABLES.SAVINGS_GOALS)
      .update(updates)
      .eq("id", goalId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Savings Ledger
  getSavingsLedger: async (childId) => {
    const { data, error } = await supabase
      .from(TABLES.SAVINGS_LEDGER)
      .select("*")
      .eq("child_id", childId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data;
  },

  getSavingsLedgerByFamily: async (familyId, { allPages = false } = {}) => {
    const createQuery = () => {
      let query = supabase.from(TABLES.SAVINGS_LEDGER)
        .select('*', allPages ? { count: 'exact' } : {})
        .eq('family_id', familyId)
        .order('created_at', { ascending: false });
      if (allPages) query = query.order('id', { ascending: false });
      return query;
    };
    if (allPages) return readAllRows(createQuery);
    const { data, error } = await createQuery();
    if (error) throw error;
    return data;
  },

  createSavingsEntry: async (entryData) => {
    const { data, error } = await supabase
      .from(TABLES.SAVINGS_LEDGER)
      .insert(entryData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Chores
getChores: async (
  familyId,
  { includeInactive = false } = {}
) => {
  let query = supabase
    .from(TABLES.CHORES)
    .select("*")
    .eq("family_id", familyId)
    .order("title");

  // Existing behavior remains the default.
  // Most screens only need active chores.
  if (!includeInactive) {
    query = query.eq(
      "is_active",
      true
    );
  }

  const { data, error } =
    await query;

  if (error) {
    throw error;
  }

  return data;
},

  createChore: async (choreData) => {
    const { data, error } = await supabase
      .from(TABLES.CHORES)
      .insert(choreData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  updateChore: async (choreId, updates) => {
    const { data, error } = await supabase
      .from(TABLES.CHORES)
      .update(updates)
      .eq("id", choreId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Chore Claims
  getChoreClaims: async (
  familyId,
  status = null
) => {
  let query = supabase
    .from(TABLES.CHORE_CLAIMS)
    .select(
      `
      *,
      child:${TABLES.CHILDREN}(
        id,
        nickname,
        user_id,
        status
      ),
      chore:${TABLES.CHORES}(
        id,
        title,
        value_cents,
        assigned_child_id,
        created_by,
        created_by_child_id,
        source
      ),
      transaction:${TABLES.TRANSACTIONS}(
        id,
        amount_cents,
        approval_status,
        category,
        subtype,
        note,
        created_at
      )
      `
    )
    .eq(
      "family_id",
      familyId
    )
    .order(
      "claimed_at",
      {
        ascending: false,
      }
    );

  if (status) {
    query = query.eq(
      "status",
      status
    );
  }

  const {
    data,
    error,
  } = await query;

  if (error) {
    throw error;
  }

  return data;
},

  createChoreClaim: async (claimData) => {
    const { data, error } = await supabase
      .from(TABLES.CHORE_CLAIMS)
      .insert(claimData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  updateChoreClaim: async (claimId, updates) => {
    const { data, error } = await supabase
      .from(TABLES.CHORE_CLAIMS)
      .update(updates)
      .eq("id", claimId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

    // --------------------------------------------------
  // Review Chore Claim
  // --------------------------------------------------
  //
  // Atomically updates BOTH:
  //
  // 1. chore_claims.status
  // 2. transactions.approval_status
  //
  // PostgreSQL validates that:
  //
  // - caller is an authenticated parent
  // - claim belongs to that parent's family
  // - claim is still pending
  // - transaction belongs to same child/family
  // - transaction is still pending
  //
  // BOTH updates succeed or BOTH roll back.
  reviewChoreClaim: async (
    claimId,
    decision
  ) => {
    const { data, error } =
      await supabase.rpc(
        'review_chore_claim',
        {
          p_claim_id: claimId,
          p_decision: decision,
        }
      );

    if (error) {
      diagnostic('error', 'family.chore_review_failed');

      throw new Error(
        error.message ||
          'Unable to review chore claim.'
      );
    }

    if (!data?.success) {
      throw new Error(
        data?.message ||
          'Unable to review chore claim.'
      );
    }

    return data;
  },
}; // ✅ IMPORTANT: closes db object

// --------------------
// Kid-only helpers (separate client + storage key)
// --------------------
export const kidAuth = {
  signIn: (email, password) =>
    supabaseKid.auth.signInWithPassword({
      email,
      password,
    }),

  signOut: async () => {
    const { detachPush } = await import('./pushNotifications');
    await detachPush(supabaseKid, 'kid');
    return supabaseKid.auth.signOut();
  },

  getUser: () =>
    supabaseKid.auth.getUser(),

  getSession: () =>
    supabaseKid.auth.getSession(),

  onAuthStateChange: (callback) =>
    supabaseKid.auth.onAuthStateChange(
      callback
    ),
};

// --------------------
// Kid authentication / profile helpers
// --------------------
export const dbKid = {
  /*
   * ---------------------------------------------------------
   * KID LOGIN
   * ---------------------------------------------------------
   *
   * Calls the protected kid_login Edge Function.
   *
   * Supabase wraps non-2xx Edge Function responses inside
   * FunctionsHttpError. The useful HTTP response is available
   * through error.context.
   *
   * Preserve the HTTP status and safe server message so the
   * Kid Login UI can distinguish:
   *
   * 400 - malformed login information
   * 401 - invalid credentials
   * 429 - rate limited
   * 500 - server problem
   */
  kidLogin: async ({
  loginId,
  password,
}) => {
  const normalizedLoginId =
    String(
      loginId || ''
    )
      .trim()
      .toUpperCase();

  const {
    data,
    error,
  } =
    await supabaseKid.functions.invoke(
      'kid_login',
      {
        body: {
          loginId:
            normalizedLoginId,

          password,
        },
      }
    );

  if (error) {
    let status =
      error?.context?.status ??
      null;

    let serverMessage =
      null;

    try {
      const response =
        error?.context;

      if (
        response &&
        typeof response.clone ===
          'function'
      ) {
        const clonedResponse =
          response.clone();

        const contentType =
          clonedResponse.headers?.get(
            'content-type'
          ) || '';

        if (
          contentType.includes(
            'application/json'
          )
        ) {
          const responseBody =
            await clonedResponse.json();

          serverMessage =
            responseBody?.error ||
            responseBody?.message ||
            null;
        } else {
          const responseText =
            await clonedResponse.text();

          if (
            responseText?.trim()
          ) {
            serverMessage =
              responseText.trim();
          }
        }
      }
    } catch (
      responseReadError
    ) {
      diagnostic('error', 'kid.login.response_parse_failed');
    }

    const loginError =
      new Error(
        serverMessage ||
          'Unable to sign in.'
      );

    loginError.status =
      status;

    loginError.code =
      status === 429
        ? 'KID_LOGIN_RATE_LIMITED'
        : status === 401
          ? 'KID_LOGIN_INVALID_CREDENTIALS'
          : status === 400
            ? 'KID_LOGIN_INVALID_REQUEST'
            : 'KID_LOGIN_FAILED';

    diagnostic('error', 'kid.auth.login_failed');

    throw loginError;
  }

  if (
    !data?.email ||
    !data?.password
  ) {
    const responseError =
      new Error(
        'Invalid login response.'
      );

    responseError.status =
      500;

    responseError.code =
      'KID_LOGIN_INVALID_RESPONSE';

    throw responseError;
  }

  return data;
},

  /*
   * ---------------------------------------------------------
   * GET KID PROFILE BY AUTH USER
   * ---------------------------------------------------------
   *
   * maybeSingle prevents 406/PGRST116 when no matching active
   * child profile exists.
   */
  getKidByAuthUserId: async (
    authUserId
  ) => {
    diagnostic('debug', 'kid.profile.load_started');

    const controller =
      new AbortController();

    const timeoutId =
      setTimeout(
        () =>
          controller.abort(),
        8000
      );

    try {
      let query =
        supabaseKid
          .from(
            TABLES.CHILDREN
          )
          .select(
            'id,family_id,auth_user_id,user_id,first_name,middle_initial,last_name,suffix,full_name,nickname,age,age_group,avatar,avatar_url,status,created_at,deactivated_at,deactivated_by_parent_id,reactivated_at,reactivated_by_parent_id,require_chore_approval,dashboard_theme,onboarding_status,onboarding_step,onboarding_version,onboarding_started_at,onboarding_completed_at,starting_money_set_at,starting_money_transaction_id'
          )
          .eq(
            'auth_user_id',
            authUserId
          )
          .eq(
            'status',
            'active'
          )
          .maybeSingle();

      query =
        query.abortSignal(
          controller.signal
        );

      const {
        data,
        error,
      } =
        await query;

      diagnostic(error ? 'warn' : 'debug', error ? 'kid.profile.load_failed' : 'kid.profile.load_completed');

      if (error) {
        throw error;
      }

      return data;
    } finally {
      clearTimeout(
        timeoutId
      );
    }
  },
};

// --------------------
// Kid DB helpers (IMPORTANT: use the kid client)
//
// Why this exists:
// Kid pages must query using the kid session token. If they call the parent
// client (`db`), PostgREST will often respond with:
//   - AuthSessionMissing (no kid session on the parent client)
//   - 406 / PGRST116 when `.single()` is used and 0 rows are returned
// Keeping a dedicated kid DB surface prevents “session clobbering” bugs.
// --------------------
export const dbKidData = {
  getMyKidBillingSummary: async () => {
    const { data, error } = await supabaseKid.rpc(
      'get_my_kid_billing_summary'
    );

    if (error) throw error;

    if (!Array.isArray(data) || data.length !== 1) {
      throw new Error(
        'Unable to determine the family subscription status.'
      );
    }

    return data[0];
  },

  resolveMyFamilyAccessState: async () => {
    const {
      data,
      error,
    } = await supabaseKid.rpc(
      'resolve_my_family_access_state'
    );

    if (error) {
      throw error;
    }

    if (
      !Array.isArray(data) ||
      data.length !== 1
    ) {
      throw new Error(
        'Unable to determine the family access state.'
      );
    }

    return data[0];
  },

  updateMyKidOnboarding: async (
    status,
    step
  ) => {
    const { data, error } = await supabaseKid.rpc(
      'update_my_kid_onboarding',
      {
        p_status: status,
        p_step: step,
      }
    );

    if (error) throw error;
    return data;
  },

  updateMyKidAvatar: async (avatar) => {
    const { data, error } = await supabaseKid.rpc(
      'update_my_kid_avatar',
      {
        p_avatar: avatar,
      }
    );

    if (error) throw error;
    return data;
  },

  setMyKidStartingMoney: async (
    amountCents
  ) => {
    const { data, error } = await supabaseKid.rpc(
      'set_my_kid_starting_money',
      {
        p_amount_cents: amountCents,
      }
    );

    if (error) throw error;
    return data;
  },

  matchMyKidPiggyBank: async (
    countedAmountCents
  ) => {
    const { data, error } = await supabaseKid.rpc(
      'match_my_kid_piggy_bank',
      {
        p_counted_amount_cents:
          countedAmountCents,
      }
    );

    if (error) throw error;
    return data;
  },

  getKidSettingsSummary: async () => {
    const { data, error } = await supabaseKid.rpc(
      'get_my_kid_settings_summary'
    );

    if (error) {
      throw new Error(
        error.message ||
          'Unable to load kid settings.'
      );
    }

    return data;
  },

  // Dashboard theme preference (kid-scoped)
  updateDashboardTheme: async (theme) => {
    const { data, error } = await supabaseKid.rpc(
      'update_kid_dashboard_theme',
      {
        p_theme: theme,
      }
    );

    if (error) {
      throw new Error(
        error.message ||
          'Unable to save dashboard theme.'
      );
    }

    if (!data?.success) {
      throw new Error(
        data?.error ||
          'Unable to save dashboard theme.'
      );
    }

    return data;
  },

  // Transactions (kid-scoped)
  getTransactions: async (familyId, childId = null) => {
    let query = supabaseKid
      .from(TABLES.TRANSACTIONS)
      .select("*")
      .eq("family_id", familyId)
      .order("created_at", { ascending: false });

    if (childId) query = query.eq("child_id", childId);

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  createPersonalChore: async (payload) => {
  const { data, error } = await supabaseKid
    .from(TABLES.CHORES)
    .insert([payload])
    .select('*')
    .single();

  if (error) throw error;
  return data;
},
  // --------------------------------------------------
  // Complete Chore
  // --------------------------------------------------
  //
  // Creates BOTH:
  //   1. chore income transaction
  //   2. chore claim
  //
  // PostgreSQL performs the operation atomically.
  //
  // IMPORTANT:
  // We intentionally send ONLY the chore ID.
  //
  // The database determines:
  // - authenticated child
  // - family
  // - chore value
  // - whether the chore belongs to this kid
  // - whether parent approval is required
  // - transaction approval status
  // - claim status
  //
  // This prevents the browser from controlling
  // sensitive chore/reward information.
  completeChore: async (choreId) => {
    const { data, error } = await supabaseKid.rpc(
      'complete_kid_chore',
      {
        p_chore_id: choreId,
      }
    );

    if (error) {
      diagnostic('error', 'kid.chore_complete_failed');

      throw new Error(
        error.message ||
          'Unable to complete chore.'
      );
    }

    if (!data?.success) {
      throw new Error(
        data?.message ||
          'Unable to complete chore.'
      );
    }

    return data;
  },
  
  createTransaction: async (transactionData) => {
    const { data, error } = await supabaseKid
      .from(TABLES.TRANSACTIONS)
      .insert(transactionData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Savings Goals (kid-scoped)
  getSavingsGoals: async (childId) => {
    const { data, error } = await supabaseKid
      .from(TABLES.SAVINGS_GOALS)
      .select("*")
      .eq("child_id", childId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data;
  },

  createSavingsGoal: async (goalData) => {
    const { data, error } = await supabaseKid.rpc(
      'create_kid_savings_goal',
      {
        p_name: goalData.name,
        p_target_amount_cents: goalData.target_amount_cents,
      }
    );
    if (error) throw error;
    return data;
  },

  updateSavingsGoal: async (goalId, updates) => {
    const { data, error } = await supabaseKid
      .from(TABLES.SAVINGS_GOALS)
      .update(updates)
      .eq("id", goalId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Savings Ledger (kid-scoped)
  getSavingsLedger: async (childId) => {
    const { data, error } = await supabaseKid
      .from(TABLES.SAVINGS_LEDGER)
      .select("*")
      .eq("child_id", childId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data;
  },

  createSavingsEntry: async (entryData) => {
    if (entryData.direction !== 'deposit' || !entryData.goal_id) {
      throw new Error('Savings changes must use the protected savings workflow.');
    }

    const { data, error } = await supabaseKid.rpc(
      'deposit_kid_savings',
      {
        p_goal_id: entryData.goal_id,
        p_amount_cents: entryData.amount_cents,
      }
    );
    if (error) throw error;
    return data;
  },

  depositSavings: async (goalId, amountCents) => {
    const { data, error } = await supabaseKid.rpc('deposit_kid_savings', {
      p_goal_id: goalId,
      p_amount_cents: amountCents,
    });
    if (error) throw error;
    return data;
  },

  cancelSavingsGoal: async (goalId) => {
    const { data, error } = await supabaseKid.rpc('cancel_kid_savings_goal', {
      p_goal_id: goalId,
    });
    if (error) throw error;
    return data;
  },

  transferSavingsGoal: async (fromGoalId, toGoalId, amountCents) => {
    const { data, error } = await supabaseKid.rpc('transfer_kid_savings_goal', {
      p_from_goal_id: fromGoalId,
      p_to_goal_id: toGoalId,
      p_amount_cents: amountCents,
    });
    if (error) throw error;
    return data;
  },

  completeSavingsGoal: async (goalId) => {
    const { data, error } = await supabaseKid.rpc('complete_kid_savings_goal', {
      p_goal_id: goalId,
    });
    if (error) throw error;
    return data;
  },

  // Chores (kid can view active chores in their family)
  // Rules:
  // - Parent-created chores: assigned_child_id is NULL (all kids) OR equals this kid
  // - Personal chores: created_by_child_id equals this kid (always visible to them)
  getChores: async (familyId, childId) => {
    let query = supabaseKid
      .from(TABLES.CHORES)
      .select('*')
      .eq('family_id', familyId)
      .eq('is_active', true)
      .order('title');

    if (childId) {
      query = query.or(
        `assigned_child_id.is.null,assigned_child_id.eq.${childId},created_by_child_id.eq.${childId}`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },
  // Family settings (kid-scoped)
getFamilySettings: async (familyId) => {
  const { data, error } = await supabaseKid
    .from(TABLES.FAMILIES)
    .select(
      'id, require_income_approval, currency, time_zone, status, deactivated_at, deletion_scheduled_for'
    )
    .eq('id', familyId)
    .maybeSingle();

  if (error) throw error;

  return data;
},

// Chore Claims (kid-scoped)
getMyChoreClaims: async (familyId, childId) => {
  const { data, error } = await supabaseKid
    .from(TABLES.CHORE_CLAIMS)
    .select('*')
    .eq('family_id', familyId)
    .eq('child_id', childId)
    .order('claimed_at', { ascending: false });
  if (error) throw error;
  return data;
},
  // Chore Claims (kid can create their own claim)
  createChoreClaim: async (claimData) => {
    const { data, error } = await supabaseKid
      .from(TABLES.CHORE_CLAIMS)
      .insert(claimData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// --------------------
// Kid debug helper (kid client)
// --------------------
export const debugRestPingKid = async () => {
  diagnostic('debug', 'kid.auth.rest_check_started');

  await supabaseKid.auth.getSession();

  const { data, error } = await supabaseKid.from(TABLES.FAMILIES).select("id").limit(1);

  diagnostic(error ? 'warn' : 'debug', error ? 'kid.auth.rest_check_failed' : 'kid.auth.rest_check_completed');

  if (error) throw error;
  return data;
};


